import React from 'react';
import { formatters, buildPredeterminedReturnSeries } from 'services';
import type { Metrics } from 'state';
import {
  LIMIT_METRIC_VAR,
  LIMIT_METRIC_SHARPE,
  LIMIT_METRIC_TREYNOR,
  LIMIT_METRIC_ALPHA,
  LIMIT_METRIC_BETA,
  LIMIT_METRIC_VOLATILITY,
  LIMIT_METRIC_MDD,
  LIMIT_METRIC_PORTFOLIO,
  REAL_TIME_SCENARIO,
} from 'state';

import type { Studio2Player, Studio2Session } from '../../live/types';
import BandChip from '../BandChip';
import KpiTile from '../KpiTile';
import RiskReturnScatter from '../RiskReturnScatter';
import type { ScatterPoint } from '../RiskReturnScatter';
import MetricEvolutionChart from '../MetricEvolutionChart';
import Dv01EvolutionChart from '../Dv01EvolutionChart';
import type { RelativizeDate } from '../../helpers';
import {
  analyzedLabel,
  formatAnalyzed,
  isRiskMetric,
  signClass,
  pct,
  money,
  num,
} from '../../helpers';

type Props = {
  player: Studio2Player;
  cohort: Studio2Player[];
  session: Studio2Session;
  // Self (trainee) view: pre-built, identity-stripped scatter points. When
  // present these are used verbatim (the cohort prop is just [self], so it can't
  // build a meaningful scatter); when absent the trainer view builds named
  // points from the full cohort below.
  scatterPoints?: ScatterPoint[];
  /** The trainee/self surface shares this panel but must not expose the DV01 stat cards. */
  showAfsDv01?: boolean;
  /** Trainee historical view: formats DV01 chart dates as relative Day N labels. */
  relativizeDate?: RelativizeDate;
};

/** One evolution chart. `key: null` ⇒ use player_history (return); otherwise
 *  read `player.metrics[].<key>`. Names + limit_index mirror the metrics-tab
 *  `metricsNameDict` so real data links in cleanly. Beta and Treynor live in
 *  the Metrics series (not the analysed-metric catalog), so they are charted
 *  here but don't appear in the coach-read list below. */
type ChartSpec = {
  key: keyof Metrics | null;
  limit_index: number;
  name: string;
};
const CHART_SPECS: ChartSpec[] = [
  { key: null, limit_index: LIMIT_METRIC_PORTFOLIO, name: 'Portfolio Return' },
  {
    key: 'sharpe_ratio',
    limit_index: LIMIT_METRIC_SHARPE,
    name: 'Sharpe Ratio',
  },
  {
    key: 'treynor_ratio',
    limit_index: LIMIT_METRIC_TREYNOR,
    name: 'Treynor Ratio',
  },
  { key: 'alpha', limit_index: LIMIT_METRIC_ALPHA, name: 'Alpha' },
  { key: 'beta', limit_index: LIMIT_METRIC_BETA, name: 'Beta' },
  { key: 'mdd', limit_index: LIMIT_METRIC_MDD, name: 'Maximum Drawdown' },
  { key: 'var05', limit_index: LIMIT_METRIC_VAR, name: 'VaR' },
  {
    key: 'portfolio_volatility',
    limit_index: LIMIT_METRIC_VOLATILITY,
    name: 'Volatility',
  },
];

// Mirrors metricInputSettings[*].player_graph_formatter from the metrics tab.
// Real `var05` is a MONETARY value (see W2) — formatted with `formatters.million`,
// matching the real metrics tab's VaR player_graph_formatter.
const GRAPH_FMT: Record<number, (v: number) => string> = {
  [LIMIT_METRIC_VAR]: v => formatters.million(v),
  [LIMIT_METRIC_SHARPE]: v => formatters.commas2Digits(v),
  [LIMIT_METRIC_TREYNOR]: v => formatters.commas2Digits(v),
  [LIMIT_METRIC_ALPHA]: v => `${Math.round(v * 100)}%`,
  [LIMIT_METRIC_BETA]: v => formatters.commas2Digits(v),
  [LIMIT_METRIC_VOLATILITY]: v => `${Math.round(v * 100)}%`,
  [LIMIT_METRIC_MDD]: v => `${Math.round(v * 100)}%`,
  [LIMIT_METRIC_PORTFOLIO]: v => formatters.percent1Digit(v),
};

const RiskPanel: React.FC<Props> = ({
  player,
  cohort,
  session,
  scatterPoints,
  showAfsDv01 = false,
  relativizeDate,
}) => {
  const m = player.last_metrics;
  const latestAfsDv01 = player.latest_afs_dv01;
  const latestAfsDv01Sub = latestAfsDv01
    ? `As of ${new Date(latestAfsDv01.scenario_date).toLocaleDateString(
        'en-GB',
        {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        },
      )}`
    : 'No snapshot';
  const freq = session.frequency;

  // Historical charts index the x-axis by day number ("Day N"), because the
  // stored `timestamp` there is just the wall-clock compute time. Real-time
  // sessions carry a genuine per-datapoint datetime (metrics: market EOD @ 23:30
  // UTC; player_history: hourly wall-clock), so plot those on a real datetime
  // axis. Mirrors the Snapshot tab's Return-vs-Benchmark chart. Historical path
  // is untouched (keeps `i*freq+1` + numeric axis + "Day N").
  const isRealTime = session.scenario_type === REAL_TIME_SCENARIO;
  // Predetermined-intervals: ONLY the "Portfolio Return" chart (spec.key === null) gets the
  // knitted anchors+fills series (Phase 1 fills carry no risk metrics -- the other specs stay
  // period-sparse on the ordinal axis until Phase-2 metric fills exist).
  const isPredetermined =
    session.time_advancement_type === 'predetermined_intervals';
  const knittedReturn = isPredetermined
    ? buildPredeterminedReturnSeries(player, session, session.display_surface)
        .portfolio
    : null;

  // Self view passes anonymised points; trainer view derives named points from
  // the cohort (same scaling the scatter used previously: raw fraction → %, 2dp).
  const points: ScatterPoint[] =
    scatterPoints ??
    cohort.map(p => ({
      x: Number((p.last_metrics.portfolio_volatility * 100).toFixed(2)),
      y: Number((p.last_portfolio_return * 100).toFixed(2)),
      isSelf: p.id === player.id,
      label: p.name,
    }));

  const evoDataFor = (spec: ChartSpec) =>
    spec.key === null
      ? (knittedReturn ??
        player.player_history.map((h, i) => ({
          x: isRealTime ? h.timestamp : i * freq + 1,
          y: h.portfolio_return,
        })))
      : player.metrics.map((row, i) => ({
          x: isRealTime ? row.timestamp : i * freq + 1,
          y: row[spec.key as keyof Metrics] as number,
        }));

  const metrics = player.analysis.analyzed_metrics;

  return (
    <div className="studio2__risk">
      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">
            Analysed metrics &amp; coach read
          </span>
          <span className="s2-muted" style={{ fontSize: 11 }}>
            evolution over the session
          </span>
        </div>
        <div className="studio2__card-body studio2__riskmetrics-body">
          {/* Decluttered small-multiples: just the metric title + line. */}
          <div className="studio2__mgraph-grid">
            {CHART_SPECS.map(spec => {
              const limits = session.metrics_limits.find(
                l => l.metric_type === spec.limit_index,
              );
              return (
                <div className="studio2__mgraph" key={spec.name}>
                  <div className="studio2__mgraph-head">
                    <span className="studio2__mgraph-name" title={spec.name}>
                      {spec.name}
                    </span>
                  </div>
                  <div className="studio2__mgraph-chart">
                    <MetricEvolutionChart
                      data={evoDataFor(spec)}
                      metricName={spec.name}
                      showTitle={false}
                      compact
                      limits={limits}
                      valueFormatter={GRAPH_FMT[spec.limit_index]}
                      height="100%"
                      frequency={freq}
                      isDateTimeX={
                        isRealTime ||
                        (isPredetermined &&
                          spec.key === null &&
                          session.display_surface === 'trainer')
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Title · value · band · coach read for every analysed metric. */}
          <div className="studio2__metric-list">
            {metrics.map(a => {
              const valueClass = isRiskMetric(a.analyzed_metric)
                ? ''
                : signClass(a.value);
              return (
                <div className="studio2__metric-row" key={a.id}>
                  <span className="studio2__metric-name">
                    {analyzedLabel(a.analyzed_metric)}
                  </span>
                  <span className={`studio2__metric-value ${valueClass}`}>
                    {formatAnalyzed(a.analyzed_metric, a.value)}
                  </span>
                  <BandChip
                    band={a.band}
                    title={a.key_characteristic_text ?? undefined}
                  />
                  <span className="studio2__metric-text">
                    {a.key_characteristic_text ?? '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="studio2__risk-summary">
        <div
          className="studio2__tiles"
          style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))' }}
        >
          <KpiTile label="Volatility" value={pct(m.portfolio_volatility)} />
          <KpiTile label="Max DD" value={pct(m.mdd)} valueClass="s2-neg" />
          <KpiTile
            label="VaR (5%)"
            value={money(m.var05)}
            valueClass="s2-neg"
          />
          <KpiTile label="Beta" value={num(m.beta)} />
        </div>
        {showAfsDv01 && (
          <div className="studio2__tiles studio2__tiles--dv01">
            <KpiTile
              label="Net DV01"
              value={num(latestAfsDv01?.net_dv01)}
              valueClass={signClass(latestAfsDv01?.net_dv01)}
              sub={latestAfsDv01Sub}
              tooltip="Absolute magnitude of the signed AFS position DV01 sum at the latest historical snapshot"
            />
            <KpiTile
              label="Gross DV01"
              value={num(latestAfsDv01?.gross_dv01)}
              sub={latestAfsDv01Sub}
              tooltip="Sum of the absolute stored AFS position DV01 values at the latest historical snapshot"
            />
          </div>
        )}
        <div className="studio2__risk-chart-stack">
          <div className="studio2__card studio2__card--dv01-evolution">
            <div className="studio2__card-head">
              <span className="studio2__card-title">DV01 evolution</span>
            </div>
            <div className="studio2__card-body studio2__card-body--pad0">
              <Dv01EvolutionChart
                history={player.afs_dv01_history}
                netDv01Limit={session.net_dv01_limit}
                grossDv01Limit={session.gross_dv01_limit}
                currencySymbol={session.reporting_currency}
                relativizeDate={relativizeDate}
                height="100%"
              />
            </div>
          </div>
          <div className="studio2__card studio2__card--risk-scatter">
            <div className="studio2__card-head">
              <span className="studio2__card-title">
                Return vs volatility — cohort
              </span>
            </div>
            <div className="studio2__card-body studio2__card-body--pad0">
              <RiskReturnScatter points={points} height="100%" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskPanel;
